import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '../../../shared/components';
import { formatDate } from '../../../shared/utils/localization';
import { useAssetsModuleVisible } from '../../assets/assetsModule';
import catalog from '../../catalog/Catalog.module.css';
import { formatMoney } from '../invoiceForm';
import { InvoiceListItem } from '../types/Invoice';
import styles from '../Invoice.module.css';

const statusClass = (item: InvoiceListItem) => {
  if (item.isOverdue) return styles.statusOverdue;
  if (item.status === 'draft') return styles.statusDraft;
  if (item.status === 'partiallyPaid') return styles.statusPartial;
  if (item.status === 'paid') return styles.statusPaid;
  if (item.status === 'void') return styles.statusVoid;
  return styles.statusIssued;
};

type InvoiceListTableProps = {
  items: InvoiceListItem[];
  emptyLabel: string;
  onOpen: (item: InvoiceListItem) => void;
  showLocation?: boolean;
  compact?: boolean;
};

export const InvoiceListTable: React.FC<InvoiceListTableProps> = ({ items, emptyLabel, onOpen, showLocation, compact }) => {
  const { t } = useTranslation(['invoices', 'common']);
  const isAssetsModuleVisible = useAssetsModuleVisible();

  if (items.length === 0) {
    return <div className={catalog.emptyState}>{emptyLabel}</div>;
  }

  return (
    <Grid
      className={compact ? styles.compactGrid : undefined}
      rowClassName={compact ? styles.compactRow : undefined}
      bodyCellClassName={compact ? styles.compactCell : undefined}
      columns={[
        {
          key: 'number',
          label: t('number', { ns: 'invoices' }),
          className: styles.numberColumn,
          headerClassName: styles.numberColumn,
          render: (item: InvoiceListItem) => (
            <div className={catalog.valueStack}>
              <span className={catalog.primaryText}>{item.number || t('statusDraft', { ns: 'invoices' })}</span>
              <span className={catalog.secondaryText}>
                {item.documentType === 'creditNote' ? t('typeCreditNote', { ns: 'invoices' }) : t('typeInvoice', { ns: 'invoices' })}
              </span>
            </div>
          ),
        },
        {
          key: 'status',
          label: t('status', { ns: 'invoices' }),
          className: styles.statusColumn,
          headerClassName: styles.statusColumn,
          render: (item: InvoiceListItem) => (
            <span className={`${catalog.statusBadge} ${statusClass(item)}`}>
              {item.isOverdue ? t('overdue', { ns: 'invoices' }) : t(`status${item.status.charAt(0).toUpperCase()}${item.status.slice(1)}`, { ns: 'invoices' })}
            </span>
          ),
        },
        {
          key: 'customer',
          label: t('customer', { ns: 'invoices' }),
          render: (item: InvoiceListItem) => (
            <div className={catalog.valueStack}>
              <span className={catalog.valueText}>{item.contactName || '-'}</span>
              {isAssetsModuleVisible && item.assetName ? <span className={catalog.secondaryText}>{item.assetName}</span> : null}
            </div>
          ),
        },
        ...(showLocation
          ? [{
              key: 'location',
              label: t('location', { ns: 'invoices' }),
              render: (item: InvoiceListItem) => (
                <span className={catalog.valueText}>{item.locationName || t('noLocation', { ns: 'invoices' })}</span>
              ),
            }]
          : []),
        ...(compact
          ? []
          : [
              {
                key: 'issueDate',
                label: t('issueDate', { ns: 'invoices' }),
                render: (item: InvoiceListItem) => <span className={catalog.valueText}>{item.issueDate ? formatDate(item.issueDate) : '-'}</span>,
              },
              {
                key: 'dueDate',
                label: t('dueDate', { ns: 'invoices' }),
                render: (item: InvoiceListItem) => <span className={catalog.valueText}>{item.dueDate ? formatDate(item.dueDate) : '-'}</span>,
              },
            ]),
        {
          key: 'total',
          label: t('total', { ns: 'invoices' }),
          className: styles.amountColumn,
          headerClassName: styles.amountColumn,
          render: (item: InvoiceListItem) => <span className={catalog.valueText}>{formatMoney(item.total, item.currency)}</span>,
        },
        {
          key: 'remaining',
          label: t('remaining', { ns: 'invoices' }),
          className: styles.amountColumn,
          headerClassName: styles.amountColumn,
          render: (item: InvoiceListItem) => <span className={catalog.valueText}>{formatMoney(item.remaining, item.currency)}</span>,
        },
      ]}
      rows={items}
      rowKey={(item: InvoiceListItem) => item.id}
      onRowClick={onOpen}
    />
  );
};
